import React, { useState } from 'react';
import { 
  Database, 
  Brain, 
  MapPin, 
  MessageSquare, 
  Image, 
  Navigation, 
  Users, 
  Star, 
  Mail, 
  Phone,
  Upload,
  Bot,
  Server,
  Globe
} from 'lucide-react';
import { motion } from 'framer-motion';

const App = () => {
  const [activeTab, setActiveTab] = useState('address');
  const [sampleData, setSampleData] = useState('');
  const [result, setResult] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState('');

  const handleSampleAnalysis = () => {
    if (activeTab === 'address' && sampleData.trim()) {
      setResult(`✅ Cleaned Address: ${sampleData.trim().replace(/\s+/g, ' ').toUpperCase()}`);
    } else if (activeTab === 'sentiment' && sampleData.trim()) {
      const positiveWords = ['great', 'excellent', 'amazing', 'love', 'perfect'];
      const negativeWords = ['terrible', 'awful', 'hate', 'bad', 'worst'];
      const text = sampleData.toLowerCase();
      
      const positiveCount = positiveWords.filter(word => text.includes(word)).length;
      const negativeCount = negativeWords.filter(word => text.includes(word)).length;
      
      let sentiment = 'Neutral';
      if (positiveCount > negativeCount) sentiment = 'Positive';
      else if (negativeCount > positiveCount) sentiment = 'Negative';
      
      setResult(`📊 Sentiment Analysis: ${sentiment} (Positive: ${positiveCount}, Negative: ${negativeCount})`);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('submitting');

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          message: formData.message
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setFormStatus('success');
        setFormData({ name: '', email: '', message: '' });
        setTimeout(() => setFormStatus(''), 5000);
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      setFormStatus('error');
    }
  };

  const services = [
    {
      icon: <Database className="w-8 h-8" />,
      title: "Fuzzy Matching",
      description: "Intelligent data deduplication and matching with advanced algorithms"
    },
    {
      icon: <MessageSquare className="w-8 h-8" />,
      title: "Sentiment Analysis",
      description: "Real-time text analysis to understand customer emotions and feedback"
    },
    {
      icon: <MapPin className="w-8 h-8" />,
      title: "Address Cleaning",
      description: "Standardize and validate addresses for accurate geolocation and delivery"
    },
    {
      icon: <Brain className="w-8 h-8" />,
      title: "AI/MCP Servers",
      description: "Scalable AI infrastructure with custom model deployment capabilities"
    },
    {
      icon: <Image className="w-8 h-8" />,
      title: "Image Recognition",
      description: "Advanced computer vision for object detection and image classification"
    },
    {
      icon: <Navigation className="w-8 h-8" />,
      title: "Route Planning",
      description: "Optimize logistics and delivery routes for maximum efficiency"
    }
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      role: "CTO, DataFlow Solutions",
      content: "dotninefive's address cleaning tool reduced our delivery errors by 85%. The API integration was seamless.",
      rating: 5
    },
    {
      name: "Michael Chen",
      role: "Founder, AnalytiCorp",
      content: "Their sentiment analysis has transformed how we understand customer feedback. Real-time insights are game-changing.",
      rating: 5
    },
    {
      name: "Emily Rodriguez",
      role: "Operations Manager, QuickShip",
      content: "Route planning optimization saved us $45K monthly in fuel costs. The team is incredibly responsive.",
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-[#2D2D2D] text-[#F9F9F9]">
      {/* Navigation */}
      <nav className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-10 h-10 bg-[#00B7C2] rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">95</span>
            </div>
            <span className="text-white text-2xl font-bold">dotninefive</span>
          </div>
          <div className="hidden md:flex items-center space-x-8">
            <a href="#about" className="text-[#F9F9F9] hover:text-[#00B7C2] transition-colors">About</a>
            <a href="#tools" className="text-[#F9F9F9] hover:text-[#00B7C2] transition-colors">Tools</a>
            <a href="#testimonials" className="text-[#F9F9F9] hover:text-[#00B7C2] transition-colors">Testimonials</a>
            <a href="#contact" className="text-[#F9F9F9] hover:text-[#00B7C2] transition-colors">Contact</a>
            <button className="bg-[#00B7C2] text-[#2D2D2D] px-6 py-2 rounded-lg hover:bg-[#00a0aa] transition-all font-semibold">
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="container mx-auto px-6 py-20 text-center">
        <motion.h1 
          className="text-5xl md:text-7xl font-bold text-[#F9F9F9] mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          Intelligent Data Solutions
        </motion.h1>
        <motion.p 
          className="text-xl text-[#CBA135] mb-12 max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          Empower your business with cutting-edge AI tools for data cleaning, analysis, and optimization. 
          Built for startups and SMBs that demand enterprise-grade solutions.
        </motion.p>
        <motion.div 
          className="flex flex-col sm:flex-row gap-4 justify-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <button className="bg-[#00B7C2] text-[#2D2D2D] px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#00a0aa] transition-all">
            Try Free Demo
          </button>
          <button className="border-2 border-[#00B7C2] text-[#F9F9F9] px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#00B7C2] hover:text-[#2D2D2D] transition-all">
            View Documentation
          </button>
        </motion.div>
      </section>

      {/* Interactive Demo Section */}
      <section className="container mx-auto px-6 py-16">
        <div className="bg-[#2D2D2D] border border-[#00B7C2] border-opacity-30 rounded-2xl p-8">
          <h2 className="text-3xl font-bold text-[#F9F9F9] text-center mb-8">Try Our Tools Live</h2>
          
          <div className="flex flex-wrap gap-4 mb-6">
            <button
              onClick={() => setActiveTab('address')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                activeTab === 'address'
                  ? 'bg-[#00B7C2] text-[#2D2D2D]'
                  : 'bg-[#2D2D2D] text-[#F9F9F9] border border-[#00B7C2] border-opacity-30 hover:border-opacity-60'
              }`}
            >
              Address Cleaning
            </button>
            <button
              onClick={() => setActiveTab('sentiment')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                activeTab === 'sentiment'
                  ? 'bg-[#00B7C2] text-[#2D2D2D]'
                  : 'bg-[#2D2D2D] text-[#F9F9F9] border border-[#00B7C2] border-opacity-30 hover:border-opacity-60'
              }`}
            >
              Sentiment Analysis
            </button>
          </div>

          <div className="space-y-4">
            <textarea
              value={sampleData}
              onChange={(e) => setSampleData(e.target.value)}
              placeholder={
                activeTab === 'address'
                  ? 'Enter messy addresses (one per line):\n123 main st, new york, ny 10001\n456 Oak Ave, Los Angeles CA 90210'
                  : 'Enter text for sentiment analysis:\nThis product is absolutely amazing! I love it so much.'
              }
              className="w-full h-32 p-4 bg-[#2D2D2D] border border-[#00B7C2] border-opacity-30 rounded-lg text-[#F9F9F9] placeholder-[#CBA135] placeholder-opacity-60 resize-none"
            />
            <div className="flex gap-4">
              <button
                onClick={handleSampleAnalysis}
                className="bg-[#00B7C2] text-[#2D2D2D] px-6 py-3 rounded-lg font-semibold hover:bg-[#00a0aa] transition-all flex items-center"
              >
                <Upload className="w-5 h-5 mr-2" />
                Analyze
              </button>
              <button
                onClick={() => {
                  setSampleData('');
                  setResult('');
                }}
                className="border-2 border-[#00B7C2] text-[#F9F9F9] px-6 py-3 rounded-lg font-semibold hover:bg-[#00B7C2] hover:text-[#2D2D2D] transition-all"
              >
                Clear
              </button>
            </div>
          </div>

          {result && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mt-6 p-4 bg-[#00B7C2] bg-opacity-20 border border-[#00B7C2] border-opacity-50 rounded-lg text-[#00B7C2]"
            >
              {result}
            </motion.div>
          )}
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="container mx-auto px-6 py-20">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-[#F9F9F9] mb-6">About dotninefive</h2>
            <p className="text-[#CBA135] mb-6">
              We're a team of data scientists, engineers, and AI specialists passionate about making 
              enterprise-grade data tools accessible to businesses of all sizes. Founded by experts 
              with decades of experience in data analytics and machine learning, we solve real-world 
              business problems with elegant, scalable solutions.
            </p>
            <p className="text-[#CBA135] mb-8">
              Our mission is to democratize access to advanced data technologies, helping startups 
              and small businesses compete with enterprise-level capabilities without the enterprise 
              price tag or complexity.
            </p>
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <Database className="w-6 h-6 text-[#00B7C2] mr-2" />
                <span className="text-[#F9F9F9]">10+ Years Experience</span>
              </div>
              <div className="flex items-center">
                <Users className="w-6 h-6 text-[#CBA135] mr-2" />
                <span className="text-[#F9F9F9]">200+ Happy Clients</span>
              </div>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-[#00B7C2] to-[#CBA135] rounded-2xl h-96 flex items-center justify-center"
          >
            <div className="text-[#2D2D2D] text-center p-8">
              <Server className="w-16 h-16 mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-2">Built for Scale</h3>
              <p className="font-medium">Enterprise-grade infrastructure with startup-friendly pricing</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Tools Section */}
      <section id="tools" className="container mx-auto px-6 py-20">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-[#F9F9F9] mb-4">Our Powerful Tools</h2>
          <p className="text-xl text-[#CBA135] max-w-2xl mx-auto">
            Comprehensive suite of AI-powered tools designed to solve your most challenging data problems
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-[#2D2D2D] border border-[#00B7C2] border-opacity-30 rounded-2xl p-6 hover:border-opacity-60 transition-all"
            >
              <div className="text-[#00B7C2] mb-4">{service.icon}</div>
              <h3 className="text-xl font-bold text-[#F9F9F9] mb-3">{service.title}</h3>
              <p className="text-[#CBA135]">{service.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="container mx-auto px-6 py-20">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-[#F9F9F9] mb-4">Trusted by Industry Leaders</h2>
          <p className="text-xl text-[#CBA135]">Hear from businesses that transformed their data operations</p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-[#2D2D2D] border border-[#CBA135] border-opacity-30 rounded-2xl p-6 hover:border-opacity-60 transition-all"
            >
              <div className="flex mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-[#CBA135]" fill="#CBA135" />
                ))}
              </div>
              <p className="text-[#CBA135] mb-6 italic">"{testimonial.content}"</p>
              <div>
                <p className="text-[#F9F9F9] font-semibold">{testimonial.name}</p>
                <p className="text-[#CBA135]">{testimonial.role}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="container mx-auto px-6 py-20">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#F9F9F9] mb-4">Ready to Transform Your Data?</h2>
            <p className="text-xl text-[#CBA135]">Get in touch with our team of experts</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12">
            <div className="space-y-8">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-[#00B7C2] rounded-lg flex items-center justify-center">
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-[#F9F9F9] font-semibold text-lg">Email Us</h3>
                  <p className="text-[#CBA135]">confidence@dotninefive.com</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-[#00B7C2] rounded-lg flex items-center justify-center">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-[#F9F9F9] font-semibold text-lg">Call Us</h3>
                  <p className="text-[#CBA135]">+0 (44) 7453-512569</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-[#00B7C2] rounded-lg flex items-center justify-center">
                  <Globe className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-[#F9F9F9] font-semibold text-lg">Schedule Demo</h3>
                  <p className="text-[#CBA135]">Book a personalized consultation</p>
                </div>
              </div>
            </div>
            
            <form onSubmit={handleFormSubmit} className="space-y-6">
              <div>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Your Name"
                  required
                  maxLength="100"
                  disabled={formStatus === 'submitting'}
                  className="w-full p-4 bg-[#2D2D2D] border border-[#00B7C2] border-opacity-30 rounded-lg text-[#F9F9F9] placeholder-[#CBA135] placeholder-opacity-60 focus:outline-none focus:border-[#00B7C2] focus:border-opacity-60 disabled:opacity-50"
                />
              </div>
              
              <div>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="Your Email"
                  required
                  maxLength="100"
                  disabled={formStatus === 'submitting'}
                  className="w-full p-4 bg-[#2D2D2D] border border-[#00B7C2] border-opacity-30 rounded-lg text-[#F9F9F9] placeholder-[#CBA135] placeholder-opacity-60 focus:outline-none focus:border-[#00B7C2] focus:border-opacity-60 disabled:opacity-50"
                />
              </div>
              
              <div>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  placeholder="Tell us about your data challenges..."
                  rows="4"
                  required
                  maxLength="1000"
                  disabled={formStatus === 'submitting'}
                  className="w-full p-4 bg-[#2D2D2D] border border-[#00B7C2] border-opacity-30 rounded-lg text-[#F9F9F9] placeholder-[#CBA135] placeholder-opacity-60 focus:outline-none focus:border-[#00B7C2] focus:border-opacity-60 resize-none disabled:opacity-50"
                ></textarea>
              </div>
              
              {formStatus === 'success' && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="p-4 bg-[#00B7C2] bg-opacity-20 border border-[#00B7C2] rounded-lg text-[#00B7C2]"
                >
                  ✅ Thank you! We'll be in touch within 24 hours.
                </motion.div>
              )}
              
              {formStatus === 'error' && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="p-4 bg-red-500 bg-opacity-20 border border-red-500 rounded-lg text-red-400"
                >
                  ❌ Something went wrong. Please try again.
                </motion.div>
              )}
              
              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full bg-[#00B7C2] text-[#2D2D2D] py-4 rounded-lg font-semibold hover:bg-[#00a0aa] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {formStatus === 'submitting' ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#00B7C2] border-opacity-30 py-8">
        <div className="container mx-auto px-6 text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <div className="w-8 h-8 bg-[#00B7C2] rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">95</span>
            </div>
            <span className="text-[#F9F9F9] text-xl font-bold">dotninefive</span>
          </div>
          <p className="text-[#CBA135]">
            © 2026 dotninefive. All rights reserved. Enterprise-grade data solutions for modern businesses.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default App;